import { useCalculatorStore } from '@/store/calculatorStore'
import Grade from './Grade'
import { Course as CourseType } from '@/data/models'
import { useCalculateCourseImprovement } from '@/hooks/useCalculations'
import { cn } from '@/lib/utils'
import { X as IconX } from 'lucide-react'

const Improvement = (course: CourseType) => {
  const improvement = Number(
    useCalculateCourseImprovement(course.id).toFixed(1)
  )

  if (improvement === 0) return null

  const sign = improvement >= 0 ? '+' : '-'
  return (
    <div
      className="flex items-center gap-1"
      data-testid={`improvement-indicator-${course.id}`}
    >
      <span
        className={cn(improvement >= 0 ? 'text-green-600' : 'text-red-600')}
        data-testid={`improvement-value-${course.id}`}
      >
        {sign}
        {Math.abs(improvement)} poeng
      </span>
    </div>
  )
}

function CourseImprovement(course: CourseType) {
  const changeGrade = useCalculatorStore((state) => state.changeGrade)
  const removeCourse = useCalculatorStore((state) => state.removeCourse)

  return (
    <div className="@container" data-testid={`course-improvement-${course.id}`}>
      <div className="@max-[700px]:pb-4 @max-[700px]:pt-2 py-3 @max-[700px]:px-5 border-b border-warm-gray-400 @min-[700px]:border-warm-gray-300 @max-[700px]:shadow-[0_4px_0_0_#F0EDEA] relative grid grid-cols-1 gap-1">
        <div className="@min-[700px]:flex @min-[700px]:items-center @min-[700px]:gap-2 text-sm">
          <div className="@max-[700px]:flex @max-[700px]:items-center @max-[700px]:justify-between relative z-10 @max-[700px]:mb-1">
            <h3 className="text-base" data-testid={`course-name-${course.id}`}>
              {course.name}
            </h3>
            <button
              onClick={() => removeCourse(course.id)}
              data-testid={`remove-course-${course.id}`}
              className="@min-[700px]:hidden text-sm text-warm-gray-700 hover:text-black hover:bg-warm-gray-200 cursor-pointer h-8 w-8 rounded-full flex items-center justify-center"
            >
              <IconX className="size-4" />
            </button>
          </div>
          <div className="hidden @min-[700px]:flex @min-[700px]:items-center @min-[700px]:gap-2 @min-[700px]:ml-auto">
            <div
              className="flex flex-row gap-1 justify-start items-center w-[100px]"
              data-testid={`existing-grades-${course.id}`}
            >
              <div className="size-8">
                <Grade
                  number={course.grade}
                  isSelected={true}
                  onClick={() => {}}
                  data-testid={`existing-grade-${course.grade}-${course.id}`}
                />
              </div>
              {course.exam > 1 && (
                <div className="size-8">
                  <Grade
                    number={course.exam}
                    isSelected={true}
                    onClick={() => {}}
                    data-testid={`existing-exam-${course.exam}-${course.id}`}
                  />
                </div>
              )}
              {course.exam2 > 1 && (
                <div className="size-8">
                  <Grade
                    number={course.exam2}
                    isSelected={true}
                    onClick={() => {}}
                    data-testid={`existing-exam2-${course.exam2}-${course.id}`}
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-6 gap-1 w-[212px]">
              {[1, 2, 3, 4, 5, 6].map((number) => (
                <Grade
                  key={number}
                  number={number}
                  isSelected={course.improved === number}
                  onClick={() =>
                    changeGrade(
                      course.id,
                      course.improved === number ? 0 : number,
                      'improved'
                    )
                  }
                  variant="improvement"
                  data-testid={`improve-grade-${number}-${course.id}`}
                />
              ))}
            </div>
            <div className="w-[80px]">
              <Improvement {...course} />
            </div>
            <div className="flex items-center justify-end w-[40px] flex-shrink-0 ml-1">
              <button
                onClick={() => removeCourse(course.id)}
                data-testid={`remove-course-${course.id}`}
                className="text-sm text-warm-gray-700 hover:text-black hover:bg-warm-gray-200 cursor-pointer h-8 w-8 rounded-full flex items-center justify-center"
              >
                <IconX className="size-4" />
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-6 gap-1 @max-[700px]:h-10 @min-[700px]:hidden">
          <div
            className="absolute top-3 right-14 flex flex-row gap-1 justify-end items-center"
            data-testid={`existing-grades-mobile-${course.id}`}
          >
            <div className="size-7">
              <Grade
                number={course.grade}
                isSelected={true}
                onClick={() => {}}
                data-testid={`existing-grade-mobile-${course.grade}-${course.id}`}
              />
            </div>
            {course.exam > 1 && (
              <div className="size-7">
                <Grade
                  number={course.exam}
                  isSelected={true}
                  onClick={() => {}}
                  data-testid={`existing-exam-mobile-${course.exam}-${course.id}`}
                />
              </div>
            )}
            {course.exam2 > 1 && (
              <div className="size-7">
                <Grade
                  number={course.exam2}
                  isSelected={true}
                  onClick={() => {}}
                  data-testid={`existing-exam2-mobile-${course.exam2}-${course.id}`}
                />
              </div>
            )}
          </div>
          {[1, 2, 3, 4, 5, 6].map((number) => (
            <Grade
              key={number}
              number={number}
              isSelected={course.improved === number}
              onClick={() =>
                changeGrade(
                  course.id,
                  course.improved === number ? 0 : number,
                  'improved'
                )
              }
              variant="improvement"
              data-testid={`improve-grade-mobile-${number}-${course.id}`}
            />
          ))}
        </div>
        {course.hasTwoExams && (
          <>
            <div className="@min-[700px]:flex @min-[700px]:items-center @min-[700px]:gap-2 text-sm">
              <div className="hidden @min-[700px]:block"></div>
              <div className="hidden @min-[700px]:flex @min-[700px]:items-center @min-[700px]:gap-2 @min-[700px]:ml-auto">
                <div className="w-[100px]"></div>
                <div className="grid grid-cols-6 gap-1 w-[212px]">
                  {[1, 2, 3, 4, 5, 6].map((number) => (
                    <Grade
                      key={number}
                      number={number}
                      isSelected={course.improved2 === number}
                      onClick={() =>
                        changeGrade(
                          course.id,
                          course.improved2 === number ? 0 : number,
                          'improved2'
                        )
                      }
                      variant="improvement"
                      data-testid={`improve-grade2-${number}-${course.id}`}
                    />
                  ))}
                </div>
                <div className="w-[80px]"></div>
                <div className="w-[40px] flex-shrink-0 ml-1"></div>
              </div>
            </div>
            <div className="grid grid-cols-6 gap-1 @max-[700px]:h-10 @min-[700px]:hidden">
              {[1, 2, 3, 4, 5, 6].map((number) => (
                <Grade
                  key={number}
                  number={number}
                  isSelected={course.improved2 === number}
                  onClick={() =>
                    changeGrade(
                      course.id,
                      course.improved2 === number ? 0 : number,
                      'improved2'
                    )
                  }
                  variant="improvement"
                  data-testid={`improve-grade2-mobile-${number}-${course.id}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default CourseImprovement
